import { FunctionComponent, MouseEvent, useEffect, useRef } from 'react';
import clsx from 'clsx';
import styles from './ServiceTypeTags.module.scss';
import { ServiceTypeTag } from './ServiceTypeTag';
import useServiceCategories from 'hooks/useServiceCategories';
import { Spinner } from 'components/UI/V3/Spinner/Spinner';

interface Props {
    showMembershipsTag?: boolean;
    membershipsTagLoading?: boolean;
    /** When true, never auto-switch to memberships (e.g. edit service: would replace the service being edited). */
    disableMembershipsAutoSelect?: boolean;
    selectedServiceType:
        | 'memberships'
        | 'grooming'
        | 'dental'
        | 'daycare'
        | 'boarding'
        | 'transportation'
        | 'walking'
        | 'training'
        | 'evaluation'
        | undefined;
    onSelectServiceType: (
        serviceType:
            | 'memberships'
            | 'grooming'
            | 'dental'
            | 'daycare'
            | 'boarding'
            | 'transportation'
            | 'walking'
            | 'training'
            | 'evaluation'
            | undefined
    ) => void;
    hideLabel?: boolean;
    /** Only these service types, instead of the ones enabled in the current location. */
    serviceTypes?: Array<string>;
    className?: string;
}

export const ServiceTypeTags: FunctionComponent<Props> = props => {
    const { hasDaycare, hasBoarding, hasTransportation, hasWalking, hasTraining } = useServiceCategories();
    const isShown = (serviceType: string, enabled: boolean) =>
        props.serviceTypes ? props.serviceTypes.includes(serviceType) : enabled;
    const containerRef = useRef<HTMLDivElement>(null);
    const membershipsPreselectDoneRef = useRef(false);
    const dragStateRef = useRef({
        isDragging: false,
        startX: 0,
        startScrollLeft: 0,
        moved: false
    });

    useEffect(() => {
        if (props.disableMembershipsAutoSelect) {
            return;
        }
        if (props.showMembershipsTag !== true) {
            membershipsPreselectDoneRef.current = false;
            return;
        }
        if (membershipsPreselectDoneRef.current) {
            return;
        }
        membershipsPreselectDoneRef.current = true;
        if (props.selectedServiceType !== 'memberships') {
            props.onSelectServiceType('memberships');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.showMembershipsTag, props.disableMembershipsAutoSelect]);

    const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0 || !containerRef.current) {
            return;
        }

        const container = containerRef.current;
        dragStateRef.current.isDragging = true;
        dragStateRef.current.startX = event.clientX;
        dragStateRef.current.startScrollLeft = container.scrollLeft;
        dragStateRef.current.moved = false;
        container.classList.add(styles.dragging);
        event.preventDefault();

        const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
            if (!dragStateRef.current.isDragging) {
                return;
            }

            const deltaX = moveEvent.clientX - dragStateRef.current.startX;

            dragStateRef.current.moved = true;
            container.scrollLeft = dragStateRef.current.startScrollLeft - deltaX;
        };

        const handleMouseUp = () => {
            dragStateRef.current.isDragging = false;
            container.classList.remove(styles.dragging);
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };

    const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
        if (dragStateRef.current.moved) {
            event.preventDefault();
            event.stopPropagation();
            dragStateRef.current.moved = false;
        }
    };

    return (
        <>
            {!props.hideLabel && (
                <div className={styles.labelRow}>
                    <label className={styles.label}>Select a Service Type</label>
                    {props.membershipsTagLoading && (
                        <span
                            className={styles.loadingSpinner}
                            aria-label="Loading memberships"
                        >
                            <Spinner size={16} />
                        </span>
                    )}
                </div>
            )}
            <div
                ref={containerRef}
                className={clsx(styles.tagContainer, props.className)}
                onMouseDown={handleMouseDown}
                onClickCapture={handleClickCapture}
            >
                {props.showMembershipsTag !== false && (
                    <ServiceTypeTag
                        name="memberships"
                        selected={props.selectedServiceType === 'memberships'}
                        onSelect={() => props.onSelectServiceType('memberships')}
                    />
                )}
                {isShown('grooming', true) && (
                    <ServiceTypeTag
                        name="grooming"
                        selected={props.selectedServiceType === 'grooming' || props.selectedServiceType === 'dental'}
                        onSelect={() => props.onSelectServiceType('grooming')}
                    />
                )}
                {isShown('daycare', hasDaycare) && (
                    <ServiceTypeTag
                        name="daycare"
                        selected={props.selectedServiceType === 'daycare'}
                        onSelect={() => props.onSelectServiceType('daycare')}
                    />
                )}
                {isShown('boarding', hasBoarding) && (
                    <ServiceTypeTag
                        name="boarding"
                        selected={props.selectedServiceType === 'boarding'}
                        onSelect={() => props.onSelectServiceType('boarding')}
                    />
                )}
                {isShown('transportation', hasTransportation) && (
                    <ServiceTypeTag
                        name="transportation"
                        selected={props.selectedServiceType === 'transportation'}
                        onSelect={() => props.onSelectServiceType('transportation')}
                    />
                )}
                {isShown('walking', hasWalking) && (
                    <ServiceTypeTag
                        name="walking"
                        selected={props.selectedServiceType === 'walking'}
                        onSelect={() => props.onSelectServiceType('walking')}
                    />
                )}
                {isShown('training', hasTraining) && (
                    <ServiceTypeTag
                        name="training"
                        selected={props.selectedServiceType === 'training'}
                        onSelect={() => props.onSelectServiceType('training')}
                    />
                )}
            </div>
        </>
    );
};

export default ServiceTypeTags;
